/* eslint-disable react/jsx-props-no-spreading */
// import * as ReactDOMClient from 'react-dom/client';
// import { createRoot } from 'react-dom/client';
import { type FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import LocationListingContext, { VIEW_MAP, type LocationListingContextProps, type LocationListingProps, VIEW_LISTING } from './LocationListingContext';
import ListingMap from './Map/ListingMap';
import LocationListingProvider from './LocationListingProvider';
import MapPanel from './Map/MapPanel';
import Switch from '2-core-elements/Switch/Switch';
import { getCleanFilters, getFilterQuery, getPageQueryURL, getQueryURL, getWhereValue, type WhereItem } from '3-components/ListingApp/ListingFilters/ListingFilterHelpers';
import { type LocationListingQuery, useLocationListingQuery } from 'cms/cms';
import { DEFAULT_FETCH_PARAMS, OPTMIZELY_GRAPHQL_BASE_URL } from 'cms/constants';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ListingError, ListingLoader } from '3-components/ListingApp/ListingUI/ListingUI';
import ListingHeader from '3-components/ListingApp/ListingHeader/ListingHeader';
import ListingContent from '3-components/ListingApp/ListingContent/ListingContent';
import { resolveLocationListing } from 'cms/resolvers/locationListingResolver';
import LocationCard from './LocationCard';
import ListingGroup from '3-components/ListingApp/ListingGroup/ListingGroup';
import type { ActiveFilter } from '3-components/ListingApp/ListingFilters/ListingFiltersTypes';


const LocationListing: FC = () => {
    const {
        locale,
        map,
        view,
        setView,
        defaultView,
        label,
        activeItemId,
        setActiveItemId,
        listing,
        setActiveFilters,
        activeFilters,
        cms,
        page,
        setPage,
        serverData,
        // serverPageUrl,
    } = useContext(LocationListingContext) as LocationListingContextProps;

    const parsedServerData = serverData && typeof serverData === 'string' ? JSON.parse(serverData) : serverData;
    const [isClient, setIsClient] = useState(false);

    const updateFilters = (value: ActiveFilter[]): void => {
        setPage(0);
        setActiveFilters(value);
    };

    const hasMap = !!map.apiKey;

    // console.info('LocationListing render', view);

    const activeViewSetting = (view === VIEW_MAP && hasMap) ? map : listing;
    const { filterByAncestors } = activeViewSetting;
    const { search, dateacquired, ...restParams } = getFilterQuery(activeFilters, activeViewSetting.filters);
    const scrollToEl = useRef<HTMLDivElement>(null);

    const whereItems: WhereItem[] = [];
    if (search) {
        whereItems.push({
            type: 'search',
            field: '_fulltext',
            value: search,
        });
    }

    if (dateacquired) {
        whereItems.push({
            type: 'year',
            field: 'DateAcquired',
            value: dateacquired,
        });
    }

    if (filterByAncestors) {
        whereItems.push({
            type: 'ancestors',
            field: 'Ancestors',
            value: filterByAncestors,
        });
    }

    const where = getWhereValue(whereItems);

    const { data, error, isLoading, isError, isFetching } = useLocationListingQuery({
        endpoint: `${cms.apiUrl ? cms.apiUrl : OPTMIZELY_GRAPHQL_BASE_URL}${cms.apiKey}`,
        fetchParams: DEFAULT_FETCH_PARAMS,
    }, {
        locale,
        where,
        skip: 0,
        limit: 100,
        // skip: view === VIEW_MAP ? 0 : page * listing.itemsPerPage,
        // limit: view === VIEW_MAP ? 100 : listing.itemsPerPage,
        ...restParams,
    }, {

        queryKey: ['location-listing'],
        keepPreviousData: true,
        staleTime: 1000 * 60 * 5,
        initialData: !isClient && parsedServerData?.data ? parsedServerData.data : undefined,
        select: useCallback((result: LocationListingQuery) => {
            return resolveLocationListing(result, {
                emailLabel: label.emailLabel,
                telLabel: label.telLabel,
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []), // https://tkdodo.eu/blog/react-query-data-transformations
    });

    useEffect(() => {
        setIsClient(true);
    }, []);

    useEffect(() => {
        window.history.replaceState({}, '', getQueryURL(activeFilters, activeViewSetting.filters));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters]);

    useEffect(() => {
        window.history.replaceState({}, '', getPageQueryURL(page));
    }, [page]);

    useEffect(() => {
        const restQuery = view === defaultView ? { view: undefined } : { view };
        window.history.replaceState({}, '', getQueryURL([], [], restQuery));
    }, [view, defaultView]);

    if (isLoading) {
        return (
            <ListingLoader loadingMessage={label.loadingMessage} />
        );
    }

    if (isError || !data) {
        console.error(error);

        return (
            <ListingError errorMessage={label.errorMessage} />
        );
    }

    return (
        <div className={`location-listing-container container ${activeItemId ? 'pin-selected' : ''}`} ref={scrollToEl}>
            <div className="location-listing-header">
                <div className="location-listing-heading">{label.title}</div>
                {hasMap && (
                    <div className="location-listing-utility">
                        <Switch
                            size="lg"
                            offLabel={label.mapViewSwitchTitle}
                            onLabel={label.listViewSwitchTitle}
                            checked={view === VIEW_LISTING}
                            id="location-listing-switch"
                            onChange={(nextChecked: boolean): void => {
                                const nextView = nextChecked ? VIEW_LISTING : VIEW_MAP;
                                const activeNextViewFilters = nextView === VIEW_LISTING ? listing.filters : map.filters;

                                // remove all unnecesary filters on query URL
                                window.history.replaceState({}, '', getQueryURL(getCleanFilters(activeViewSetting.filters || []), activeViewSetting.filters || []));

                                // set active filters for cleaned version of it
                                updateFilters(getCleanFilters(activeNextViewFilters || []));

                                // clear the active item id
                                setActiveItemId('');

                                // then change the view
                                setView(nextView);
                            }}
                        />
                    </div>
                )}
            </div>
            {label.filterHeading && activeViewSetting.filters && (
                <ListingHeader
                    filters={activeViewSetting.filters}
                    activeFilters={activeFilters}
                    setActiveFilters={updateFilters}
                    title={label.filterHeading}
                    clearFilterLabel={label.clearFilterButton}
                />
            )}
            <div className="location-listing-content">
                {view === VIEW_MAP && hasMap && (
                    <div className="map-layout">
                        <div className="map-column">
                            <ListingMap
                                apiKey={map.apiKey}
                                mapId={map.mapId}
                                items={data.mapItems}
                            />
                        </div>
                        <div className="map-info-column">
                            <div className="map-info-column-content">
                                <MapPanel items={data.mapItems} />
                            </div>
                        </div>
                    </div>
                )}
                {view === VIEW_LISTING && (
                    <ListingContent
                        isFetching={isFetching}
                        isNoItems={!data.cardGroups.length}
                        // pagination={{
                        //     itemsPerPage: listing.itemsPerPage,
                        //     page,
                        //     setPage,
                        //     totalItems: data.total,
                        // }}
                        loadingLabel={label.loadingMessage}
                        noItemsLabel={label.noItemMessage}
                        cols="1"
                    >
                        {data.cardGroups.map(({ title, cardItems }, groupIndex) => {
                            const groupKey = `${title}-${groupIndex}`;

                            return (
                                <ListingGroup key={groupKey} title={title}>
                                    {cardItems.map((props, i) => {
                                        const key = `${props.title}-${i}-${props.link?.href}`;
                                        return (
                                            <LocationCard key={key} {...props} />
                                        );
                                    })}
                                </ListingGroup>
                            );
                        })}
                    </ListingContent>
                )}
            </div>
        </div>
    );
};

const LocationListingWithProvider: FC<LocationListingProps> = props => {
    // eslint-disable-next-line react/hook-use-state
    const [queryClient] = useState(() => {
        return new QueryClient();
    });

    useEffect(() => {
        return () => {
            queryClient.clear(); // Clean up on unmount
        };
    }, [queryClient]);

    // console.info('LocationListingWithProvider render', props);

    return (
        <QueryClientProvider client={queryClient}>
            <LocationListingProvider {...props}>
                <LocationListing />
            </LocationListingProvider>
        </QueryClientProvider>
    );
};

export default LocationListingWithProvider;
